/**
 * Plan / Cycles / Review — historical look-back for a single closed
 * forecast cycle.
 *
 * Plan workspace is "now": current open cycle, editable, forward-
 * looking. This page is "then": pick a closed cycle, see the frozen
 * forecast vs what actually came in, read-only.
 *
 * Shell only in this commit — picker + auto-pinned target plan +
 * empty body. Variance table and chart land in follow-up commits.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { AlertTriangle } from "lucide-react";

import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { ForecastCycle } from "@shared-core/data/adapter";
import type * as API from "@shared-core/data/api-types";
import {
  CURRENCY_VIEW_LABELS,
  SCENARIO_LABELS,
  type CurrencyView,
  type ScenarioKind,
} from "@shared-core/domain/plan-pickers";
import {
  aroundForecastDate,
  bucketDates,
  toISODate,
  zoomToFit,
  type Period,
} from "@shared-core/domain/plan-range";
import { fmtM } from "@shared-core/domain/format-money";
import { LayeredTimelineChart } from "../app/components/domain/forecasting/LayeredTimelineChart";

import { Card } from "../app/components/primitives/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { WorkspaceNav } from "../app/components/domain/forecasting/WorkspaceNav";
import { useUrlPickerState } from "../app/components/domain/forecasting/use-url-picker-state";
import { paths } from "@shared-core/routing/routes";
import { currentTime } from "@shared-core/domain/current-time";

// Review page windows: "Around forecast date" (default — 3mo pre-as-of
// to today) and "Custom" (explicit from/to with to clamped at today).
// FY / Rolling don't fit here — they include future dates which are
// irrelevant when we're asking "what actually happened vs what we said".
type ReviewWindowKind = "around" | "custom";

export function PlanCycleReviewPage() {
  const { cycleId } = useParams<{ cycleId?: string }>();
  const navigate = useNavigate();
  const adapter = useDataAdapter();

  const [cycles, setCycles] = useState<ForecastCycle[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Cycle-review pickers — cycle picker is primary, the others are
  // axes the user can tweak. Window defaults to "around" (3 months
  // pre-as-of → today, the common comparison view).
  // Picker state lives in the URL so reload / share-by-link works.
  const [currencyRaw, setCurrency] = useUrlPickerState(
    "currency",
    "booked_contribution",
  );
  const currency = currencyRaw as CurrencyView;
  const [scenarioRaw, setScenario] = useUrlPickerState(
    "scenario",
    "most_likely",
  );
  const scenario = scenarioRaw as ScenarioKind;
  const [windowModeRaw, setWindowMode] = useUrlPickerState("window", "around");
  const windowMode = windowModeRaw as ReviewWindowKind;
  const [periodRaw, setPeriod] = useUrlPickerState("period", "monthly");
  const period = periodRaw as Period;
  const [customFrom, setCustomFrom] = useUrlPickerState("from", "");
  const [customTo, setCustomTo] = useUrlPickerState(
    "to",
    toISODate(currentTime()),
  );

  // Load all cycles (closed-only filtered below).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await adapter.listForecastCycles();
        if (!cancelled) setCycles(list);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  const closedCycles = useMemo(
    () =>
      cycles
        .filter((c) => c.closedAt !== null)
        .sort((a, b) => (b.closedAt ?? "").localeCompare(a.closedAt ?? "")),
    [cycles],
  );

  const selectedCycle = useMemo(
    () => closedCycles.find((c) => c.id === cycleId) ?? null,
    [closedCycles, cycleId],
  );

  // Auto-redirect: if no cycleId in URL and we have closed cycles,
  // land on the most-recently-closed one.
  useEffect(() => {
    if (!cycleId && closedCycles.length > 0) {
      navigate(paths.planCycleReview({ cycleId: closedCycles[0].id }), {
        replace: true,
      });
    }
  }, [cycleId, closedCycles, navigate]);

  // Load the cycle's plan-grid (cycle_id scope) — gives us at-publish
  // forecast (frozen) alongside actuals-since (current FY actuals,
  // independent of submission scope).
  const [grid, setGrid] = useState<API.PlanGrid | null>(null);
  const [gridError, setGridError] = useState<string | null>(null);
  // Seed custom from/to from the "Around forecast date" range when
  // the selected cycle changes — so switching the picker to Custom
  // doesn't briefly land on empty dates.
  useEffect(() => {
    if (!selectedCycle) return;
    const around = aroundForecastDate(selectedCycle.asOfDate);
    setCustomFrom(around.from);
    setCustomTo(around.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed-on-cycle-change effect; the custom-range setters are stable
  }, [selectedCycle]);

  // Effective range — Review always sends Custom to the backend
  // (the window=around picker resolves locally to a from/to pair).
  // Clamps the future side at today + applies zoom-to-fit so we
  // never exceed 120 buckets.
  const todayIso = toISODate(currentTime());
  const effectiveRange = useMemo(() => {
    if (!selectedCycle) return null;
    const base =
      windowMode === "around"
        ? aroundForecastDate(selectedCycle.asOfDate)
        : { from: customFrom, to: customTo };
    // Review-page always clamps the future at today.
    const clampedTo = base.to > todayIso ? todayIso : base.to;
    return zoomToFit({ from: base.from, to: clampedTo }, period, {
      anchor: selectedCycle.asOfDate,
      clampMaxTo: todayIso,
    });
  }, [selectedCycle, windowMode, customFrom, customTo, period, todayIso]);

  useEffect(() => {
    if (!selectedCycle || !effectiveRange) return;
    let cancelled = false;
    setGrid(null);
    setGridError(null);
    (async () => {
      try {
        const [v, c]: [PlanGridQueryView, PlanGridQueryCurrency] =
          splitCurrencyKey(currency);
        const scenarioApi =
          scenario === "good"
            ? "good_case"
            : scenario === "bad"
              ? "bad_case"
              : "most_likely";
        const fiscalYear = fiscalYearOf(selectedCycle.asOfDate);
        const result = await adapter.getPlanGrid(fiscalYear, {
          view: v,
          currency: c,
          scenario: scenarioApi,
          // Review always uses Custom-shaped range under the hood
          // (the "Around forecast date" preset resolves to a from/to
          // pair locally). Period drives the bucket size.
          window: "custom",
          period,
          rangeStart: effectiveRange.from,
          rangeEnd: effectiveRange.to,
          cycleId: selectedCycle.id,
        });
        if (!cancelled) setGrid(result);
      } catch (err) {
        if (!cancelled) {
          setGridError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter, selectedCycle, currency, scenario, effectiveRange, period]);

  return (
    <div className="space-y-4">
      <WorkspaceNav active="reviews" />

      {/* Work-in-progress banner — same structural pattern as the
          estimates ops-preview banner, amber tone instead of red so it
          reads as "be cautious" rather than "this is dummy data". */}
      <div className="rounded-md border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-700 dark:text-amber-400">
              Work in progress — numbers may be unreliable
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
              Cycle retrospectives are still being tuned. Some bars (chart) and
              rows (per-rep table) may not align with what reps and managers
              actually saw at close. Treat the trends as directional rather than
              the figures as definitive.
            </p>
          </div>
        </div>
      </div>

      {/* Picker bar — cycle is primary, others fine-tune the view. */}
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-surface p-3">
        <Picker
          label="Cycle"
          tooltip="Which closed cycle to review. Open cycle work lives on /plan."
          value={cycleId ?? ""}
          onChange={(v) =>
            navigate(paths.planCycleReview({ cycleId: v }), { replace: true })
          }
          options={closedCycles.map((c) => ({
            value: c.id,
            label: `${c.label || `Cycle as-of ${c.asOfDate}`} (closed ${(c.closedAt ?? "").slice(0, 10)})`,
          }))}
        />
        <Picker
          label="Currency"
          tooltip="Denomination of the displayed amounts. Booked Contribution is the operational primary."
          value={currency}
          onChange={(v) => setCurrency(v as CurrencyView)}
          options={Object.entries(CURRENCY_VIEW_LABELS).map(([k, l]) => ({
            value: k,
            label: l,
          }))}
        />
        <Picker
          label="Scenario"
          tooltip="Most Likely / Good / Bad. Useful to see how the realised number compared to the bad-case projection at close."
          value={scenario}
          onChange={(v) => setScenario(v as ScenarioKind)}
          options={Object.entries(SCENARIO_LABELS).map(([k, l]) => ({
            value: k,
            label: l,
          }))}
        />
        <Picker
          label="Window"
          tooltip="Around forecast date = 3 months pre-as-of → today (the comparison view). Custom = pick from / to (to clamped at today). FY / Rolling don't fit here — they include future dates that have no signal on this screen."
          value={windowMode}
          onChange={(v) => setWindowMode(v as ReviewWindowKind)}
          options={[
            { value: "around", label: "Around forecast date" },
            { value: "custom", label: "Custom" },
          ]}
        />
        <Picker
          label="Period"
          tooltip="Bucket granularity. Monthly is the operational default; Weekly and Daily zoom in (capped at 120 buckets). Target/Prior-year overlays drop off below monthly — both depend on per-month source data."
          value={period}
          onChange={(v) => setPeriod(v as Period)}
          options={[
            { value: "monthly", label: "Monthly" },
            { value: "weekly", label: "Weekly" },
            { value: "daily", label: "Daily" },
          ]}
        />
        {windowMode === "custom" && (
          <div className="flex items-center gap-2 text-sm">
            <label className="flex items-center gap-1">
              <span className="text-muted-foreground">From:</span>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-8 px-2 border border-border rounded-md bg-background"
              />
            </label>
            <label className="flex items-center gap-1">
              <span className="text-muted-foreground">To:</span>
              <input
                type="date"
                value={customTo}
                max={todayIso}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-8 px-2 border border-border rounded-md bg-background"
              />
            </label>
          </div>
        )}
      </div>

      {/* Cycle context strip — closed-cycle equivalent of CycleStatusRow. */}
      {selectedCycle && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="font-medium">
            {selectedCycle.label || `Cycle as-of ${selectedCycle.asOfDate}`}
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">
            closed {selectedCycle.closedAt?.slice(0, 10) ?? ""}
          </span>
          {selectedCycle.closedBy ? (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground text-xs">
                by {selectedCycle.closedByName ?? selectedCycle.closedBy}
              </span>
            </>
          ) : null}
          {selectedCycle.notes ? (
            <span className="text-xs italic text-muted-foreground">
              {selectedCycle.notes}
            </span>
          ) : null}
        </div>
      )}

      {loadError && (
        <Card className="p-4 border-red-200 bg-red-50/50 text-sm text-red-900">
          Failed to load cycles: {loadError}
        </Card>
      )}

      {!loadError && closedCycles.length === 0 && (
        <Card className="p-6 text-sm text-muted-foreground">
          No closed cycles yet — the first cycle has to close before there's
          anything to look back at.
        </Card>
      )}

      {selectedCycle && gridError && (
        <Card className="p-4 border-red-200 bg-red-50/50 text-sm text-red-900">
          Failed to load retrospective data: {gridError}
        </Card>
      )}

      {selectedCycle &&
        grid &&
        effectiveRange &&
        !gridError &&
        (() => {
          // Resolve as-of / today to bucket indices using the same
          // period + range the backend used. bucketDates gives us bucket
          // start dates in order. The actuals array itself is already
          // pre-as-of-zeroed server-side when cycle_id is set, so no
          // duplicate masking is needed here — only the marker indices.
          const buckets = bucketDates(effectiveRange, period);
          const indexFor = (iso: string): number | undefined => {
            if (buckets.length === 0) return undefined;
            // Last bucket whose start ≤ iso.
            let idx = -1;
            for (let i = 0; i < buckets.length; i++) {
              if (buckets[i] <= iso) idx = i;
              else break;
            }
            return idx >= 0 ? idx : undefined;
          };
          const asOfIndex = indexFor(selectedCycle.asOfDate);
          const todayIndex = indexFor(todayIso);
          return (
            <>
              <Card className="p-4">
                <h2 className="text-sm font-semibold mb-3">
                  Cycle overlay vs actuals
                </h2>
                <LayeredTimelineChart
                  title=""
                  monthLabels={grid.month_labels}
                  baseline={grid.company.baseline_by_month ?? Array(12).fill(0)}
                  adjustment={
                    grid.company.adjustment_by_month ?? Array(12).fill(0)
                  }
                  pipeline={grid.company.pipeline_by_month ?? Array(12).fill(0)}
                  actuals={grid.company.actuals_by_month ?? Array(12).fill(0)}
                  priorYear={
                    grid.company.prior_year_by_month ?? Array(12).fill(0)
                  }
                  height="lg"
                  asOfIndex={asOfIndex}
                  todayIndex={todayIndex}
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Bars show what was forecast at this cycle's close (baseline +
                  adjustments + pipeline). Actuals on the bars are what's come
                  in since the as-of date — pre-as-of actuals are excluded
                  because they were already inside the cycle's baseline.
                </p>
              </Card>

              <Card className="overflow-x-auto">
                <div className="p-3 border-b border-border flex items-baseline justify-between">
                  <h2 className="text-sm font-semibold">
                    Per-rep variance (FY total)
                  </h2>
                  <span className="text-xs text-muted-foreground">
                    Forecast and target are as-at cycle close. Actuals are
                    what's come in since.
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-surface-hover">
                    <tr>
                      <th className="text-left p-2">Rep</th>
                      <th className="text-right p-2">Target</th>
                      <th className="text-right p-2">At-publish forecast</th>
                      <th className="text-right p-2">Actuals so far</th>
                      <th className="text-right p-2">Variance £</th>
                      <th className="text-right p-2">Variance %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grid.reps.map((rep) => {
                      const tgt = rep.fy.target;
                      const fcst = rep.fy.adjusted_forecast;
                      const act = (rep.actuals_by_month ?? []).reduce(
                        (a, b) => a + b,
                        0,
                      );
                      const variance = act - fcst;
                      const variancePct =
                        fcst > 0 ? (variance / fcst) * 100 : 0;
                      return (
                        <VarianceRow
                          key={rep.user_id}
                          name={rep.name}
                          target={tgt}
                          forecast={fcst}
                          actuals={act}
                          variance={variance}
                          variancePct={variancePct}
                        />
                      );
                    })}
                    <tr className="border-t-2 border-border bg-surface-hover/50 font-semibold">
                      <td className="p-2">Company</td>
                      <td className="text-right p-2">
                        {fmtM(grid.company.target)}
                      </td>
                      <td className="text-right p-2">
                        {fmtM(grid.company.adjusted_forecast)}
                      </td>
                      <td className="text-right p-2">
                        {fmtM(
                          (grid.company.actuals_by_month ?? []).reduce(
                            (a, b) => a + b,
                            0,
                          ),
                        )}
                      </td>
                      <td className="text-right p-2">
                        {(() => {
                          const v =
                            (grid.company.actuals_by_month ?? []).reduce(
                              (a, b) => a + b,
                              0,
                            ) - grid.company.adjusted_forecast;
                          return signed(fmtM(Math.abs(v)), v >= 0);
                        })()}
                      </td>
                      <td className="text-right p-2">
                        {(() => {
                          const f = grid.company.adjusted_forecast;
                          if (!f) return "—";
                          const a = (
                            grid.company.actuals_by_month ?? []
                          ).reduce((s, b) => s + b, 0);
                          const pct = ((a - f) / f) * 100;
                          return signed(
                            `${Math.abs(pct).toFixed(1)}%`,
                            pct >= 0,
                          );
                        })()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Card>
            </>
          );
        })()}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────

type PlanGridQueryView = "booked" | "invoiced";
type PlanGridQueryCurrency = "contribution" | "revenue";

/** Split a CurrencyView like "booked_contribution" → ["booked", "contribution"]. */
function splitCurrencyKey(
  c: CurrencyView,
): [PlanGridQueryView, PlanGridQueryCurrency] {
  const [v, ...rest] = c.split("_");
  return [v as PlanGridQueryView, rest.join("_") as PlanGridQueryCurrency];
}

/** UK fiscal year (Apr-Mar) for a given as-of ISO date. */
function fiscalYearOf(isoDate: string): number {
  const d = new Date(isoDate);
  return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
}

function signed(value: string, positive: boolean): React.ReactNode {
  return (
    <span className={positive ? "text-emerald-700" : "text-destructive"}>
      {positive ? "+" : "-"}
      {value}
    </span>
  );
}

function VarianceRow({
  name,
  target,
  forecast,
  actuals,
  variance,
  variancePct,
}: {
  name: string;
  target: number;
  forecast: number;
  actuals: number;
  variance: number;
  variancePct: number;
}) {
  return (
    <tr className="border-t border-border">
      <td className="p-2">{name}</td>
      <td className="text-right p-2 text-muted-foreground">{fmtM(target)}</td>
      <td className="text-right p-2">{fmtM(forecast)}</td>
      <td className="text-right p-2">{fmtM(actuals)}</td>
      <td className="text-right p-2">
        {forecast > 0 ? signed(fmtM(Math.abs(variance)), variance >= 0) : "—"}
      </td>
      <td className="text-right p-2">
        {forecast > 0
          ? signed(`${Math.abs(variancePct).toFixed(1)}%`, variancePct >= 0)
          : "—"}
      </td>
    </tr>
  );
}

function Picker({
  label,
  tooltip,
  value,
  onChange,
  options,
}: {
  label: string;
  tooltip: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string; disabled?: boolean }[];
}) {
  return (
    <label className="flex items-center gap-2 text-sm" title={tooltip}>
      <span className="text-muted-foreground">{label}:</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 min-w-[10rem]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}
